import { useMemo, useState } from "react";
import { api } from "../lib/api";
import { toDateTime, lossReasonLabel } from "../lib/helpers";
import { X, Download, Trash2, Loader2, Camera, ShieldAlert } from "lucide-react";

async function readBlobError(e) {
  try {
    const t = await e.response.data.text();
    return JSON.parse(t).detail;
  } catch {
    return e?.response?.data?.detail || e.message || "Something went wrong.";
  }
}

// Admin-only: export selected LOST leads (data + images as a ZIP) then permanently
// delete exactly what was exported. Deletion stays disabled until the matching set
// has been exported.
export default function LostCleanupDialog({ lostLeads, onClose, onDeleted }) {
  const [mode, setMode] = useState("pick"); // "pick" | "date"
  const [beforeDate, setBeforeDate] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [exportedSig, setExportedSig] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState("");

  const targetLeads = useMemo(() => {
    if (mode === "date") {
      if (!beforeDate) return [];
      const cutoff = new Date(beforeDate + "T00:00:00");
      return lostLeads.filter((l) => l.created_at && new Date(l.created_at) < cutoff);
    }
    return lostLeads.filter((l) => picked.has(l.id));
  }, [mode, beforeDate, picked, lostLeads]);

  const targetIds = useMemo(() => targetLeads.map((l) => l.id), [targetLeads]);
  const sig = useMemo(() => targetIds.slice().sort().join(","), [targetIds]);
  const totalPhotos = useMemo(
    () => targetLeads.reduce((n, l) => n + (l.photo_count ?? l.photos?.length ?? 0), 0),
    [targetLeads]
  );
  const exportedMatches = exportedSig !== null && exportedSig === sig && targetIds.length > 0;

  const toggle = (id) => {
    setExportedSig(null);
    setPicked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const onExport = async () => {
    if (!targetIds.length) return;
    setErr("");
    setExporting(true);
    try {
      const r = await api.post("/leads/lost/export", { lead_ids: targetIds }, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "");
      a.href = url;
      a.download = `lost_leads_export_${ts}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      setExportedSig(sig);
    } catch (e) {
      setErr(await readBlobError(e));
    } finally {
      setExporting(false);
    }
  };

  const onDelete = async () => {
    if (!exportedMatches) return;
    if (!confirm(`Permanently delete the ${targetIds.length} lost lead(s) you just exported? This cannot be undone.`)) return;
    setErr("");
    setDeleting(true);
    try {
      const r = await api.post("/leads/lost/delete", { lead_ids: targetIds });
      onDeleted(r.data.deleted);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="lost-cleanup-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Export &amp; clean up lost leads</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100" data-testid="lost-cleanup-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-4">
          <p className="text-sm text-slate-500">
            Download an archive (Excel + photos) of old lost leads, then permanently delete them to free up space. Deletion only works on what you just exported.
          </p>

          {/* Mode selector */}
          <div className="flex gap-2">
            <button
              onClick={() => { setMode("pick"); setExportedSig(null); }}
              className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg border transition-colors ${mode === "pick" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200"}`}
              data-testid="cleanup-mode-pick"
            >
              Pick specific leads
            </button>
            <button
              onClick={() => { setMode("date"); setExportedSig(null); }}
              className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg border transition-colors ${mode === "date" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200"}`}
              data-testid="cleanup-mode-date"
            >
              All older than a date
            </button>
          </div>

          {mode === "date" ? (
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Delete lost leads created before</label>
              <input
                type="date"
                value={beforeDate}
                onChange={(e) => { setBeforeDate(e.target.value); setExportedSig(null); }}
                className="sohom-input w-44"
                data-testid="cleanup-before-date"
              />
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-slate-100" data-testid="cleanup-lead-list">
              {lostLeads.length === 0 ? (
                <div className="text-sm text-slate-400 italic p-4 text-center">No lost leads.</div>
              ) : lostLeads.map((l) => (
                <label key={l.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`cleanup-pick-${l.lead_no}`}>
                  <input type="checkbox" checked={picked.has(l.id)} onChange={() => toggle(l.id)} className="w-4 h-4 accent-red-600" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      <span className="font-mono text-xs text-slate-500 mr-2">{l.lead_no}</span>
                      {l.building_name || "Unnamed"}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {lossReasonLabel(l.loss_reason)} · {toDateTime(l.created_at).split(" ")[0]}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Camera className="w-3 h-3" />{l.photo_count ?? l.photos?.length ?? 0}</span>
                </label>
              ))}
            </div>
          )}

          {/* Summary */}
          <div className="bg-slate-50 rounded-lg p-3 text-sm" data-testid="cleanup-summary">
            <span className="font-semibold text-slate-900">{targetIds.length}</span> lead(s) selected
            <span className="text-slate-400"> · </span>
            <span className="inline-flex items-center gap-1 text-slate-600"><Camera className="w-3.5 h-3.5" />{totalPhotos} photo(s)</span>
          </div>

          {err && (
            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3" data-testid="cleanup-error">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /> {err}
            </div>
          )}
        </div>

        {/* Steps footer */}
        <div className="px-5 py-4 border-t border-slate-200 space-y-2">
          <div className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center font-bold shrink-0">1</span>
            <button
              onClick={onExport}
              disabled={targetIds.length === 0 || exporting}
              className="sohom-btn-secondary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cleanup-export-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {exporting ? "Preparing archive…" : `Download export (${targetIds.length})`}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold shrink-0 ${exportedMatches ? "bg-red-600 text-white" : "bg-slate-200 text-slate-400"}`}>2</span>
            <button
              onClick={onDelete}
              disabled={!exportedMatches || deleting}
              className="sohom-btn-primary flex-1 justify-center bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cleanup-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {deleting ? "Deleting…" : `Delete ${targetIds.length} exported lead(s) permanently`}
            </button>
          </div>
          {!exportedMatches && targetIds.length > 0 && (
            <p className="text-xs text-slate-400 pl-9">Download the export first to unlock deletion.</p>
          )}
        </div>
      </div>
    </div>
  );
}
